import { IntlShape } from 'react-intl'
import { formatMultipleSelectData } from './formatMultipleSelectData'
import { getCountryCodeByCountryId } from './convertCountryCodesToCountries'
import {
  convertTimeZoneDateTimeToUtc,
  convertUtcDateTimeToTimeZone,
  getTimeZoneById,
  TIMEZONE_ID_HELSINKI,
} from './timeZone'
import { type ChallengeCreateAndUpdateData, ChallengeTypeConfig, ChallengeTypeConfigType } from 'api/types'

export type ChallengePayload = ChallengeCreateAndUpdateData

export const getChallengeTypeName = (challengeType?: ChallengeTypeConfigType, intl?: IntlShape): string => {
  return intl ? intl.formatMessage({ id: `challengeType.${challengeType}` }) : ''
}

export const getSponsoredChallengeTypeName = (challengeType: Challenge['typeConfig'], intl: IntlShape): string => {
  return challengeType ? intl.formatMessage({ id: `challengeType.${challengeType.type}` }) : ''
}

export const getSponsoredChallengePointType = (challengePointType: string, intl: IntlShape): string => {
  return challengePointType ? intl.formatMessage({ id: `challengeType.${challengePointType}` }) : ''
}

export enum ChallengeCountType {
  TOTAL = 'TOTAL',
  BEST = 'BEST',
}

export enum ChallengeQuickInsightCalculation {
  CHALLENGE_TYPE_UNSPECIFIED = 'points',
  CHALLENGE_TYPE_NUMBER_OF_HOLES_PLAYED = 'holes',
  CHALLENGE_TYPE_NUMBER_OF_COURSES_PLAYED = 'courses',
  CHALLENGE_TYPE_NUMBER_OF_PARS = 'pars',
  CHALLENGE_TYPE_BIRDIES_OR_BETTER = 'birdies',
  CHALLENGE_TYPE_STROKEPLAY = 'scores',
  CHALLENGE_TYPE_STABLEFORD = 'points',
}

export enum ChallengeReasonRule {
  ROUND_ELIGIBILITY_RULE_UNSPECIFIED = 'new rule with out title',
  ROUND_ELIGIBILITY_RULE_ROUND_NOT_DELETED = `Round isn't deleted`,
  ROUND_ELIGIBILITY_RULE_ROUND_NOT_PRIVATE = `Rountd isn't private`,
  ROUND_ELIGIBILITY_RULE_ROUND_NOT_SIMULATOR = `Round isn't a simulator round`,
  ROUND_ELIGIBILITY_RULE_COURSE_PAR_AT_LEAST = 'Course par is at least {min_par}',
  ROUND_ELIGIBILITY_RULE_HOLES_PLAYED_AT_LEAST = 'At least {min_holes} holes played',
  ROUND_ELIGIBILITY_RULE_FULL_ROUND_WITH_ALL_VALID_SCORES = 'Every hole has a score',
  ROUND_ELIGIBILITY_RULE_ROUND_GROSS_STROKES_AT_LEAST = 'At least {min_gross_strokes} gross strokes',
  ROUND_ELIGIBILITY_RULE_ROUND_NET_STROKES_AT_LEAST = 'At least {min_net_strokes} net strokes',
  ROUND_ELIGIBILITY_RULE_ROUND_STROKES_AT_LEAST = 'Score is high enough to count',
  ROUND_ELIGIBILITY_RULE_ROUND_STABLEFORD_POINTS_AT_MOST = 'At most {max_stableford_points} Stableford points',
  ROUND_ELIGIBILITY_RULE_ECLECTIC_COURSE = 'Course is part of this challenge',
  ROUND_ELIGIBILITY_RULE_INDIVIDUAL_ROUND_ONLY = 'Format records individual scores',
  ROUND_ELIGIBILITY_RULE_SELECTED_COURSES_ONLY = 'Round played on allowed courses',
  ROUND_ELIGIBILITY_RULE_PARTICIPANT_ACTIVE_WINDOW = 'Round played while participant',
  ROUND_ELIGIBILITY_RULE_ROUND_IS_HCP_REQUIREMENT = 'Round played as hcp round',
  ROUND_ELIGIBILITY_RULE_SELECTED_CLUBS_ONLY = 'Round played on allowed clubs',
  ROUND_ELIGIBILITY_RULE_COUNTRY_REQUIREMENT = 'Round was played in correct country',
}

export enum StablefordResultAggregationType {
  TOTAL = 'RESULT_AGGREGATION_TOTAL',
  BEST = 'RESULT_AGGREGATION_BEST',
}

export const getCountTypesForChallengeType = (
  challengeType: NonNullable<Challenge['typeConfig']>['type'],
): ChallengeCountType[] => {
  switch (challengeType) {
    case ChallengeTypeConfig.STABLEFORD:
      return [ChallengeCountType.BEST, ChallengeCountType.TOTAL]
    case ChallengeTypeConfig.STROKEPLAY:
      return [ChallengeCountType.BEST]
    case ChallengeTypeConfig.NUMBER_OF_HOLES_PLAYED:
    case ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED:
    case ChallengeTypeConfig.NUMBER_OF_PARS:
    case ChallengeTypeConfig.BIRDIES_OR_BETTER:
    case ChallengeTypeConfig.ECLECTIC:
      return [ChallengeCountType.TOTAL]
    default:
      return []
  }
}

export const getDefaultMinPar = (challengeType: NonNullable<Challenge['typeConfig']>['type']): number | undefined => {
  switch (challengeType) {
    case ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED:
      return 54
    case ChallengeTypeConfig.NUMBER_OF_HOLES_PLAYED:
      return 54
    case ChallengeTypeConfig.STABLEFORD:
    case ChallengeTypeConfig.STROKEPLAY:
    case ChallengeTypeConfig.BIRDIES_OR_BETTER:
    case ChallengeTypeConfig.NUMBER_OF_PARS:
    case ChallengeTypeConfig.ECLECTIC:
      return 69
    default:
      return
  }
}

export enum ChallengeGameFormat {
  GOLF_GAME_FORMAT_UNSPECIFIED = '-',
  GOLF_GAME_FORMAT_STROKE_PLAY_INDIVIDUAL = 'Strokeplay',
  GOLF_GAME_FORMAT_STROKE_PLAY_BETTER_BALL = 'Better ball',
  GOLF_GAME_FORMAT_STROKE_PLAY_BEST_BALL = 'Best ball',
  GOLF_GAME_FORMAT_STROKE_PLAY_SCRAMBLE = 'Scramble',
  GOLF_GAME_FORMAT_STROKE_PLAY_SCRAMBLE2MAN = 'Scramble 2-man',
  GOLF_GAME_FORMAT_STROKE_PLAY_ERADO = 'Erado',
  GOLF_GAME_FORMAT_STROKE_PLAY_SKINS = 'Skins',
  GOLF_GAME_FORMAT_STROKE_PLAY_LOW_SCRATCH_NET = 'Low scratch net',
  GOLF_GAME_FORMAT_STROKE_PLAY_GREENSOME = 'Greensome',
  GOLF_GAME_FORMAT_STROKE_PLAY_FOURSOME = 'Goursome',
  GOLF_GAME_FORMAT_STROKE_PLAY_COLLEGE = 'Stroke play college',
  GOLF_GAME_FORMAT_STROKE_PLAY_SPLIT_POINTS = 'Stroke play split points',
  GOLF_GAME_FORMAT_STABLEFORD_INDIVIDUAL = 'Stableford',
  GOLF_GAME_FORMAT_STABLEFORD_INDIVIDUAL_DUPLICATE = 'Duplicate',
  GOLF_GAME_FORMAT_STABLEFORD_BETTER_BALL = 'Stableford better ball',
  GOLF_GAME_FORMAT_STABLEFORD_BEST_BALL = 'Stableford best ball',
  GOLF_GAME_FORMAT_STABLEFORD_SCRAMBLE_DUPLICATE = 'Scramble duplicate',
  GOLF_GAME_FORMAT_STABLEFORD_GREENSOME = 'Stableford greensome',
  GOLF_GAME_FORMAT_STABLEFORD_FOURSOME = 'Stableford foursome',
  GOLF_GAME_FORMAT_IRISH_RUMBLE_BEST_BALL = 'Irish rumble',
  GOLF_GAME_FORMAT_MATCH_PLAY_INDIVIDUAL = 'Match play individual',
  GOLF_GAME_FORMAT_MATCH_PLAY_BETTER_BALL = 'Match play better ball',
  GOLF_GAME_FORMAT_MATCH_PLAY_SCRAMBLE = 'Match play scramble',
  GOLF_GAME_FORMAT_MATCH_PLAY_GREENSOME = 'Match play greensome',
  GOLF_GAME_FORMAT_MATCH_PLAY_FOURSOME = 'Match play foursome',
  GOLF_GAME_FORMAT_MATCH_PLAY_HIGH_LOW = 'Match play high low',
}

export enum ChallengePrivilege {
  ADMINISTRATOR = 'USER_PRIVILEGE_ADMINISTRATOR',
  AMBASSADOR = 'USER_PRIVILEGE_AMBASSADOR',
  GOLD_MEMBERSHIP = 'USER_PRIVILEGE_GOLD_MEMBERSHIP',
  DEVELOPER = 'USER_PRIVILEGE_DEVELOPER',
  FEATURED_STARS_TIER_1 = 'USER_PRIVILEGE_FEATURED_STARS_TIER_1',
  FEATURED_STARS_TIER_2 = 'USER_PRIVILEGE_FEATURED_STARS_TIER_2',
  FEATURED_STARS_TIER_3 = 'USER_PRIVILEGE_FEATURED_STARS_TIER_3',
  FEATURED_STARS_TIER_4 = 'USER_PRIVILEGE_FEATURED_STARS_TIER_4',
}

export enum ChallengePrivilegeToFormData {
  USER_PRIVILEGE_ADMINISTRATOR = 'ADMINISTRATOR',
  USER_PRIVILEGE_AMBASSADOR = 'AMBASSADOR',
  USER_PRIVILEGE_GOLD_MEMBERSHIP = 'GOLD_MEMBERSHIP',
  USER_PRIVILEGE_DEVELOPER = 'DEVELOPER',
  USER_PRIVILEGE_FEATURED_STARS_TIER_1 = 'FEATURED_STARS_TIER_1',
  USER_PRIVILEGE_FEATURED_STARS_TIER_2 = 'FEATURED_STARS_TIER_2',
  USER_PRIVILEGE_FEATURED_STARS_TIER_3 = 'FEATURED_STARS_TIER_3',
  USER_PRIVILEGE_FEATURED_STARS_TIER_4 = 'FEATURED_STARS_TIER_4',
}

export enum ChallengeComputedState {
  UNSPECIFIED = 'CHALLENGE_COMPUTED_STATE_UNSPECIFIED',
  DRAFT = 'CHALLENGE_COMPUTED_STATE_DRAFT',
  UPCOMING = 'CHALLENGE_COMPUTED_STATE_UPCOMING',
  VISIBLE = 'CHALLENGE_COMPUTED_STATE_VISIBLE',
  LIVE = 'CHALLENGE_COMPUTED_STATE_LIVE',
  COMPLETED = 'CHALLENGE_COMPUTED_STATE_COMPLETED',
  UNPUBLISHED = 'CHALLENGE_COMPUTED_STATE_UNPUBLISHED',
}

export enum ChallengeControlledState {
  UNSPECIFIED = 'CHALLENGE_CONTROLLED_STATE_UNSPECIFIED',
  DRAFT = 'CHALLENGE_CONTROLLED_STATE_DRAFT',
  PUBLISHED = 'CHALLENGE_CONTROLLED_STATE_PUBLISHED',
  UNPUBLISHED = 'CHALLENGE_CONTROLLED_STATE_UNPUBLISHED',
}

export enum GolfRoundType {
  UNSPECIFIED = 'GOLF_ROUND_TYPE_UNSPECIFIED',
  SOCIAL_GENERAL_PLAY_ROUND = 'GOLF_ROUND_TYPE_SOCIAL_GENERAL_PLAY_ROUND',
  HCP_GENERAL_PLAY_ROUND = 'GOLF_ROUND_TYPE_HCP_GENERAL_PLAY_ROUND',
  HCP_COMPETITION_PLAY_ROUND = 'GOLF_ROUND_TYPE_HCP_COMPETITION_PLAY_ROUND',
  SIMULATOR_ROUND = 'GOLF_ROUND_TYPE_SIMULATOR_ROUND',
}

export enum ChallengeInfoContentType {
  CHALLENGE_INFO_CONTENT_TYPE_UNSPECIFIED = 'CHALLENGE_INFO_CONTENT_TYPE_UNSPECIFIED',
  CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT = 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
  CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT = 'CHALLENGE_INFO_CONTENT_TYPE_RICH_TEXT',
}

export const mapUserChallengeToFormData = (challenge: Challenge, clubs: Club[], countries: Country[]) => {
  return {
    ...challenge,
    roundEligibility: {
      ...challenge.roundEligibility,
      countriesInclusive: !!challenge?.roundEligibility?.countriesInclusive,
      clubIds: challenge.roundEligibility?.clubIds
        ?.map((clubId) => {
          const club = clubs.find((club) => club.id === Number(clubId))
          return {
            key: club?.id ?? '',
            label: club?.name || '',
          }
        })
        .filter((club) => club.label),
      courseIds: challenge?.roundEligibility?.courseIds
        ?.map((courseId) => {
          let courseLabel = ''
          clubs.forEach((club) => {
            const course = club.courses.find((c) => c.id === Number(courseId))
            if (course) {
              courseLabel = course.name
            }
          })
          return {
            key: courseId,
            label: courseLabel,
          }
        })
        .filter((course) => course.label),
      countries: challenge?.roundEligibility?.countries?.map((countryCode) => {
        const country = countries.find((c) => c.countryCode === countryCode)
        return {
          key: country ? country.id : '',
          label: country ? country.name : '',
        }
      }),
    },
  }
}

// The form keeps the scoring selection as a flat `scoringType` field, while the API stores it as
// the format specific values under typeConfig, so it has to be derived back when loading.
export const getScoringTypeFromTypeConfig = (typeConfig: Challenge['typeConfig']): string => {
  switch (typeConfig?.type) {
    case ChallengeTypeConfig.STROKEPLAY:
      if (!typeConfig.strokeplay) {
        return ''
      }
      return typeConfig.strokeplay.useHandicap ? 'net' : 'gross'
    case ChallengeTypeConfig.BIRDIES_OR_BETTER: {
      const birdiesOrBetter = typeConfig.birdiesOrBetter
      if (!birdiesOrBetter) {
        return ''
      }
      // The form only offers the two presets, so equal values are the all-one-point preset
      const { birdieValue, eagleValue, hioValue, albatrossValue } = birdiesOrBetter
      const allOnePoint = birdieValue === eagleValue && eagleValue === hioValue && hioValue === albatrossValue

      return allOnePoint ? 'allOnePoint' : 'pointLadder'
    }
    default:
      return ''
  }
}

// Only stableford stores the count on the challenge. Every other format allows a single count type,
// so for those it follows from the challenge type itself.
export const getCalculationTypeFromTypeConfig = (typeConfig: Challenge['typeConfig']): ChallengeCountType | '' => {
  if (typeConfig?.type === ChallengeTypeConfig.STABLEFORD) {
    switch (typeConfig.stableford?.resultAggregation) {
      case StablefordResultAggregationType.TOTAL:
        return ChallengeCountType.TOTAL
      case StablefordResultAggregationType.BEST:
        return ChallengeCountType.BEST
      default:
        return ''
    }
  }

  const allowedCountTypes = getCountTypesForChallengeType(typeConfig?.type)

  return allowedCountTypes.length === 1 ? allowedCountTypes[0] : ''
}

// Date inputs only accept YYYY-MM-DD, but the API returns a full timestamp
const mapBirthdateLimitToFormData = (birthdateLimit: string | undefined) => {
  return birthdateLimit ? birthdateLimit.split('T')[0] : undefined
}

const mapHcpLimitToFormData = (hcpLimit: number | undefined) => {
  return typeof hcpLimit === 'number' && !isNaN(hcpLimit) ? hcpLimit : undefined
}

const mapAudienceFiltersToFormData = (audience: ChallengeAudience | undefined) => {
  return {
    birthdateFilter: {
      birthdateLowerLimit: mapBirthdateLimitToFormData(audience?.birthdateFilter?.birthdateLowerLimit),
      birthdateUpperLimit: mapBirthdateLimitToFormData(audience?.birthdateFilter?.birthdateUpperLimit),
    },
    hcpFilter: {
      minTenths: mapHcpLimitToFormData(audience?.hcpFilter?.minTenths),
      maxTenths: mapHcpLimitToFormData(audience?.hcpFilter?.maxTenths),
    },
    // The API omits the gender when it is unspecified, which would leave the select empty
    gender: audience?.gender || undefined,
  }
}

// A saved selection has to survive a missing label: the club data is fetched separately, and
// dropping the entries we cannot name would remove them from the form and, on the next save,
// from the challenge itself.
const resolveClubOption = (clubs: Club[], clubId: string): SelectOption => {
  const club = clubs.find((club) => club.id === Number(clubId))

  return {
    key: club?.id ?? Number(clubId),
    label: club?.name || String(clubId),
  }
}

export const mapChallengeToFormData = (
  challenge: Challenge,
  clubs: Club[],
  countries: Country[],
  intl: IntlShape,
  timeZones: TimeZone[] = [],
) => {
  const selectedTimeZone = getTimeZoneById(timeZones, challenge?.metadata?.timeZoneId)

  return {
    ...challenge,
    audienceControl: {
      ...challenge.audienceControl,
      joinAudience: {
        ...challenge.audienceControl?.joinAudience,
        // The API omits this flag when it is false, so it has to be normalised to a boolean here.
        // Leaving it undefined makes the form select register an empty string, which the schema
        // then rejects as a non-boolean.
        countriesInclusive: String(challenge.audienceControl?.joinAudience?.countriesInclusive) ?? 'true',
        countries: challenge.audienceControl?.joinAudience?.countries?.map((countryCode) => {
          const country = countries.find((c) => c.countryCode === countryCode)
          return {
            key: country ? country.id : '',
            label: country ? country.name : '',
          }
        }),
        clubIds: challenge.audienceControl?.joinAudience?.clubIds?.map((clubId) => resolveClubOption(clubs, clubId)),
        privileges: challenge.audienceControl?.joinAudience?.privileges?.map((privilege) => {
          return {
            key: privilege,
            label: ChallengePrivilegeToFormData[privilege],
          }
        }),
        ...mapAudienceFiltersToFormData(challenge.audienceControl?.joinAudience),
      },
      extraViewAudience: {
        ...challenge.audienceControl?.extraViewAudience,
        countriesInclusive: String(challenge.audienceControl?.extraViewAudience?.countriesInclusive ?? true),
        privileges: challenge.audienceControl?.extraViewAudience?.privileges?.map((privilege) => {
          return {
            key: privilege,
            label: intl.formatMessage({ id: `userPrivileges.${privilege}` }),
          }
        }),
        countries: challenge.audienceControl?.extraViewAudience?.countries?.map((countryCode) => {
          const country = countries.find((c) => c.countryCode === countryCode)
          return {
            key: country ? country.id : '',
            label: country ? country.name : '',
          }
        }),
        clubIds: challenge.audienceControl?.extraViewAudience?.clubIds?.map((clubId) =>
          resolveClubOption(clubs, clubId),
        ),
        ...mapAudienceFiltersToFormData(challenge.audienceControl?.extraViewAudience),
      },
    },
    roundEligibility: {
      ...challenge.roundEligibility,
      countriesInclusive: String(challenge.roundEligibility?.countriesInclusive) ?? 'true',
      clubIds: challenge.roundEligibility?.clubIds?.map((clubId) => resolveClubOption(clubs, clubId)),
      courseIds: challenge.roundEligibility?.courseIds?.map((courseId) => {
        let courseLabel = ''
        clubs.forEach((club) => {
          const course = club.courses.find((c) => c.id === Number(courseId))
          if (course) {
            courseLabel = course.name
          }
        })
        return {
          key: courseId,
          label: courseLabel || String(courseId),
        }
      }),
      countries: challenge.roundEligibility?.countries?.map((countryCode) => {
        const country = countries.find((c) => c.countryCode === countryCode)
        return {
          key: country ? country.id : '',
          label: country ? country.name : '',
        }
      }),
    },
    schedule: {
      visibleAt: convertUtcDateTimeToTimeZone(challenge.schedule?.visibleAt, selectedTimeZone),
      startAt: convertUtcDateTimeToTimeZone(challenge.schedule?.startAt, selectedTimeZone),
      endAt: convertUtcDateTimeToTimeZone(challenge.schedule?.endAt, selectedTimeZone),
    },
    // The opt-in toggle is a flat form field, the API stores it as terms.isRequired
    terms: challenge.terms,
    // Scoring, count and target are flat form fields, the API nests them under typeConfig
    scoringType: getScoringTypeFromTypeConfig(challenge.typeConfig),
    calculationType: getCalculationTypeFromTypeConfig(challenge.typeConfig),
    targetCount: challenge.typeConfig?.target !== undefined ? String(challenge.typeConfig.target) : '',
    metadata: {
      timeZoneId: !isNaN(Number(challenge?.metadata?.timeZoneId))
        ? Number(challenge?.metadata?.timeZoneId)
        : TIMEZONE_ID_HELSINKI,
    },
  }
}

// The API expects the config of the chosen game format only, so the other formats are left out
// entirely instead of being sent as empty objects.
const mapFormDataToTypeConfig = (
  typeConfig: any,
  calculationType: string,
  targetCount: string,
  courseIds: string[],
): ChallengePayload['typeConfig'] => {
  const type = typeConfig.type ?? ''
  // The field is untouched until the user edits it, so it can still be undefined here
  const target = String(targetCount ?? '').length !== 0 ? Number(targetCount) : undefined

  switch (type) {
    case ChallengeTypeConfig.STROKEPLAY:
      return {
        type,
        target,
        strokeplay: {
          useHandicap: !!typeConfig.strokeplay?.useHandicap,
        },
      }
    case ChallengeTypeConfig.BIRDIES_OR_BETTER:
      return {
        type,
        target,
        birdiesOrBetter: {
          birdieValue: typeConfig.birdiesOrBetter?.birdieValue ?? 0,
          eagleValue: typeConfig.birdiesOrBetter?.eagleValue ?? 0,
          hioValue: typeConfig.birdiesOrBetter?.hioValue ?? 0,
          albatrossValue: typeConfig.birdiesOrBetter?.albatrossValue ?? 0,
        },
      }
    case ChallengeTypeConfig.STABLEFORD:
      return {
        type,
        target,
        stableford: {
          resultAggregation: StablefordResultAggregationType[calculationType] ?? '',
        },
      }
    case ChallengeTypeConfig.ECLECTIC:
      return {
        type,
        target,
        eclectic: {
          courseIds: courseIds,
        },
      }
    default:
      // Count based formats (holes, courses and pars played) have no format specific config
      return {
        type,
        target,
      }
  }
}

export const mapFormDataToChallenge = (
  formData: any,
  countries: Country[],
  timeZones: TimeZone[] = [],
): ChallengePayload => {
  const selectedTimeZone = getTimeZoneById(timeZones, formData?.metadata?.timeZoneId ?? formData?.timeZoneId)
  const toUtcDateTime = (value?: string) => {
    if (!value) {
      return ''
    }

    return convertTimeZoneDateTimeToUtc(value, selectedTimeZone)
  }

  const roundEligibilityCourseIds = formatMultipleSelectData(formData.roundEligibility?.courseIds).map((option) =>
    String(option),
  )

  const roundEligibilityCountries = formData.roundEligibility?.countries
    .map((country) => getCountryCodeByCountryId(countries, country.key))
    .filter(Boolean)
  const roundEligibilityClubIds =
    formData.roundEligibility?.clubIds?.map((club: SelectOption) => String(club.key)) || []
  const audienceControl = formData?.audienceControl
  const joinAudience = audienceControl?.joinAudience
  const extraViewAudience = audienceControl?.extraViewAudience
  const info = formData?.info
  const typeConfig = formData?.typeConfig
  const prizeInfo = formData?.prizeInfo

  return {
    info: {
      name: info.name ?? '',
      subtitle: {
        type: info.subtitle?.type ?? 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
        content: info.subtitle?.content ?? '',
      },
      longDescription: {
        type: info.longDescription?.type ?? 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
        content: info.longDescription?.content ?? '',
      },
      additionalInformation: {
        type: info.additionalInformation?.type ?? 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
        content: info.additionalInformation?.content ?? '',
      },
    },
    audienceControl: {
      isDiscoverable: !!audienceControl.isDiscoverable,
      useJoinAudienceOnly: !!audienceControl.useJoinAudienceOnly,
      joinAudience: {
        countries: (joinAudience.countries ?? [])
          .map((country) => getCountryCodeByCountryId(countries, country.key))
          .filter(Boolean),
        countriesInclusive: !!joinAudience.countriesInclusive,
        birthdateFilter:
          joinAudience.birthdateFilter?.birthdateLowerLimit || joinAudience.birthdateFilter?.birthdateUpperLimit
            ? {
                birthdateLowerLimit: joinAudience.birthdateFilter?.birthdateLowerLimit ?? undefined,
                birthdateUpperLimit: joinAudience.birthdateFilter?.birthdateUpperLimit ?? undefined,
              }
            : undefined,
        hcpFilter: {
          minTenths: joinAudience.hcpFilter?.minTenths,
          maxTenths: joinAudience.hcpFilter?.maxTenths,
        },
        // Core does not accept the unspecified value
        // GolfGender.GOLF_GENDER_UNSPECIFIED is only used on the client side
        gender: joinAudience.gender ? joinAudience.gender : undefined,
        userSubscriptionTypes: (joinAudience.userSubscriptionTypes ?? []).map((option: unknown) => String(option)),
        privileges: formatMultipleSelectData(joinAudience.privileges).map((option) =>
          String(option),
        ) as ChallengePayload['audienceControl'] extends { joinAudience?: { privileges?: infer P } } ? P : never,
        clubIds: formatMultipleSelectData(joinAudience.clubIds).map((option) => String(option)),
      },
      extraViewAudience: {
        countries: (joinAudience.countries ?? [])
          .map((country) => getCountryCodeByCountryId(countries, country.key))
          .filter(Boolean),
        countriesInclusive: !!joinAudience.countriesInclusive,
        birthdateFilter:
          joinAudience.birthdateFilter?.birthdateLowerLimit || joinAudience.birthdateFilter?.birthdateUpperLimit
            ? {
                birthdateLowerLimit: joinAudience.birthdateFilter?.birthdateLowerLimit ?? undefined,
                birthdateUpperLimit: joinAudience.birthdateFilter?.birthdateUpperLimit ?? undefined,
              }
            : undefined,
        hcpFilter: {
          minTenths: joinAudience.hcpFilter?.minTenths,
          maxTenths: joinAudience.hcpFilter?.maxTenths,
        },
        gender: joinAudience.gender ? joinAudience.gender : undefined,
        userSubscriptionTypes: (extraViewAudience.userSubscriptionTypes ?? []).map((option: unknown) => String(option)),
        privileges: formatMultipleSelectData(joinAudience.privileges).map((option) =>
          String(option),
        ) as ChallengePayload['audienceControl'] extends { extraViewAudience?: { privileges?: infer P } } ? P : never,
        clubIds: formatMultipleSelectData(joinAudience.clubIds).map((option) => String(option)),
      },
    },
    typeConfig: mapFormDataToTypeConfig(
      typeConfig,
      formData.calculationType,
      formData.targetCount,
      roundEligibilityCourseIds,
    ),
    schedule: {
      visibleAt: toUtcDateTime(formData.schedule?.visibleAt),
      startAt: toUtcDateTime(formData.schedule?.startAt),
      endAt: toUtcDateTime(formData.schedule?.endAt),
    },
    roundEligibility: {
      clubIds: roundEligibilityClubIds,
      courseIds: roundEligibilityCourseIds,
      countries: [...roundEligibilityCountries],
      countriesInclusive: !!formData.roundEligibility?.countriesInclusive,
      minCoursePar:
        typeof formData.roundEligibility?.minCoursePar === 'number' ? formData.roundEligibility.minCoursePar : 0,
      roundTypes: (formData.roundEligibility?.roundTypes ?? []).map((roundType: unknown) => String(roundType)),
    },
    prizeInfo: {
      hasPrizes: !!prizeInfo.hasPrizes,
      shortDescription: {
        type: prizeInfo.shortDescription?.type ?? 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
        content: prizeInfo.shortDescription?.content ?? '',
      },
      longDescription: {
        type: prizeInfo.longDescription?.type ?? 'CHALLENGE_INFO_CONTENT_TYPE_PLAIN_TEXT',
        content: prizeInfo.longDescription?.content ?? '',
      },
    },
    terms:
      formData.terms?.isRequired === true
        ? {
            description: {
              type: formData.terms?.description?.type,
              content: formData.terms?.description?.content,
            },
            isRequired: formData.terms?.isRequired,
          }
        : undefined,
    metadata: {
      timeZoneId: String(formData.metadata?.timeZoneId ?? formData.timeZoneId ?? TIMEZONE_ID_HELSINKI),
    },
  }
}
